/**
 * Revenue Stats sale lines (WSRADM-0022 / WSRADM-0034).
 *
 * Server order is date descending — do not re-sort. Money uses the month-detail
 * currency. `reconciliation` is fetched on the header call and is not rendered.
 * Currency and dimension ride the URL into every fetch and breadcrumb link.
 */
import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Link, Snackbar, Typography } from '@mui/material';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatMoney, formatMonthLong } from '@/api/reports/format';
import {
  appendReportParams,
  familySlugToPath,
  fetchRevenueLines,
  fetchRevenueMonth,
  isCurrencyNotFound,
  isDimensionNotFound,
  pathToFamilySlug,
  rebuildReports,
} from '@/api/reports/reportsApi';
import type { RevenueLineRow } from '@/api/reports/types';
import { useAuth } from '@/auth/AuthContext';
import { canRebuildReports } from '@/auth/reportAccess';
import { ReportFreshness } from '@/components/ReportFreshness';
import { RosterTable, type RosterColumn } from '@/components/RosterTable';
import { EM_DASH } from '@/components/rosterFormat';
import { ErrorDetails } from '@/components/ErrorDetails';
import { useCopy } from '@/hooks/useCopy';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useResource } from '@/hooks/useResource';

const PAGE_SIZE_DEFAULT = 20;

function formatInTimeZone(value: string | null, timezone: string, locale: string): string {
  if (value === null || value.length === 0) return EM_DASH;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' };
  if (timezone.length > 0) {
    try {
      return new Intl.DateTimeFormat(locale, { ...options, timeZone: timezone }).format(parsed);
    } catch {
      // Invalid timezone — format without it. Not a staleness decision.
    }
  }
  return new Intl.DateTimeFormat(locale, options).format(parsed);
}

export function RevenueLinesPage() {
  const { t } = useCopy();
  useDocumentTitle(t('reports.revenue.lines.title', 'Sale lines'));
  const { i18n } = useTranslation();
  const { operator } = useAuth();
  const { month = '', familySlug: familyToken = '' } = useParams<{
    month: string;
    familySlug: string;
  }>();
  const familySlug = pathToFamilySlug(familyToken);
  const [searchParams, setSearchParams] = useSearchParams();
  const productKey = searchParams.get('productKey') ?? '';
  const dimension = searchParams.get('dimension') ?? '';
  const currency = searchParams.get('currency') ?? '';

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(PAGE_SIZE_DEFAULT);
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildNotice, setRebuildNotice] = useState<'ok' | 'error' | null>(null);
  const [rebuildError, setRebuildError] = useState<unknown>(null);
  // Keep the last notice kind so the snackbar content does not flip to the success
  // copy while an error is transitioning out after being dismissed.
  const [lastNotice, setLastNotice] = useState<'ok' | 'error'>('ok');
  if (rebuildNotice !== null && rebuildNotice !== lastNotice) setLastNotice(rebuildNotice);
  const shownNotice = rebuildNotice ?? lastNotice;

  const header = useResource(
    () => fetchRevenueMonth(month, { dimension, currency }),
    [month, dimension, currency],
  );
  const lines = useResource(
    () =>
      fetchRevenueLines(month, familySlug, {
        currency: header.data?.currency ?? '',
        dimension,
        productKey,
        sort: '',
        order: 'desc',
        page,
        size,
      }),
    [month, familyToken, header.data?.currency, dimension, productKey, page, size],
  );

  const canRebuild = canRebuildReports(operator?.roles);
  const responseCurrency = header.data?.currency ?? '';
  const timezone = header.data?.timezone ?? '';
  const familyName =
    header.data?.familyColumns.find((column) => column.slug === familySlug)?.displayName ??
    familyToken;
  const sep = t('reports.revenue.breadcrumb.separator', '›');
  const monthLabel = formatMonthLong(month, i18n.language);
  const productName =
    productKey.length > 0
      ? (lines.data?.content.find((row) => row.productName)?.productName ?? null)
      : null;
  const reportParams = { dimension, currency: responseCurrency || currency };
  const familyHref = appendReportParams(
    `/reports/revenue/${month}/${familySlugToPath(familySlug)}`,
    reportParams,
  );

  useEffect(() => {
    if (header.status !== 'error') return;
    const next = new URLSearchParams(searchParams);
    let changed = false;
    if (isDimensionNotFound(header.error) && dimension !== '') {
      next.delete('dimension');
      changed = true;
    } else if (isCurrencyNotFound(header.error) && currency !== '') {
      next.delete('currency');
      changed = true;
    }
    if (changed) setSearchParams(next, { replace: true });
  }, [header.status, header.error, dimension, currency, searchParams, setSearchParams]);

  const handleRebuild = () => {
    setRebuilding(true);
    void rebuildReports()
      .then(() => setRebuildNotice('ok'))
      .catch((err: unknown) => {
        setRebuildError(err);
        setRebuildNotice('error');
      })
      .finally(() => setRebuilding(false));
  };

  const fmt = (amount: number) => formatMoney(amount, responseCurrency, i18n.language);

  const columns = useMemo<RosterColumn<RevenueLineRow>[]>(
    () => [
      {
        id: 'saleDate',
        header: t('reports.revenue.lines.date', 'Date'),
        render: (row) => formatInTimeZone(row.saleDate, timezone, i18n.language),
      },
      {
        id: 'memberName',
        header: t('reports.revenue.lines.member', 'Member'),
        render: (row) => {
          if (row.sourceClientId === null) return row.memberName ?? EM_DASH;
          return (
            <Link component={RouterLink} to={`/users/${row.sourceClientId}`}>
              {row.memberName ?? EM_DASH}
            </Link>
          );
        },
      },
      {
        id: 'productName',
        header: t('reports.revenue.lines.product', 'Product'),
        render: (row) => row.productName ?? EM_DASH,
      },
      {
        id: 'quantity',
        header: t('reports.revenue.lines.qty', 'Qty'),
        render: (row) => String(row.quantity),
      },
      {
        id: 'payedPrice',
        header: t('reports.revenue.lines.unitPrice', 'Unit price'),
        render: (row) => fmt(row.payedPrice),
      },
      {
        id: 'lineAmount',
        header: t('reports.revenue.lines.amount', 'Amount'),
        render: (row) => fmt(row.lineAmount),
      },
      {
        id: 'documentNumber',
        header: t('reports.revenue.lines.document', 'Document'),
        render: (row) => (row.documentNumber === null ? EM_DASH : String(row.documentNumber)),
      },
      {
        id: 'paymentAccountName',
        header: t('reports.revenue.lines.paymentAccount', 'Payment account'),
        render: (row) => row.paymentAccountName ?? EM_DASH,
      },
    ],
    [i18n.language, responseCurrency, t, timezone],
  );

  const neverComputed = header.status === 'ready' && header.data?.neverComputed === true;

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        <Link
          component={RouterLink}
          to={appendReportParams('/reports/revenue', reportParams)}
        >
          {t('reports.revenue.breadcrumb.revenue', 'Revenue')}
        </Link>
        {` ${sep} ${monthLabel} ${sep} `}
        <Link component={RouterLink} to={familyHref}>
          {familyName}
        </Link>
        {productKey.length > 0 ? ` ${sep} ${productName ?? EM_DASH}` : null}
      </Typography>

      <Typography variant="h1" sx={{ mb: 1 }}>
        {t('reports.revenue.lines.title', 'Sale lines')}
      </Typography>
      {responseCurrency.length > 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          {t('reports.revenue.currencyCaption', 'Currency: {{code}}', {
            code: responseCurrency,
          })}
        </Typography>
      ) : (
        <Box sx={{ mb: 2.5 }} />
      )}

      {header.data ? (
        <ReportFreshness
          asOf={header.data.asOf}
          stale={header.data.stale}
          neverComputed={header.data.neverComputed}
          timezone={header.data.timezone}
          canRebuild={canRebuild}
          onRebuild={handleRebuild}
          rebuilding={rebuilding}
        />
      ) : null}

      {neverComputed ? null : (
        <RosterTable
          title={t('reports.revenue.lines.title', 'Sale lines')}
          columns={columns}
          rows={lines.data?.content ?? []}
          rowId={(row) => `${row.sourceSaleId}:${row.sourceDetailId}`}
          status={lines.status}
          error={lines.error}
          totalElements={lines.data?.totalElements ?? 0}
          page={page}
          size={size}
          onPageChange={setPage}
          onRowsPerPageChange={(next) => {
            setSize(next);
            setPage(0);
          }}
          onApply={() => {
            // Lines filter by productKey from the URL; roster search is unused.
          }}
          onRetry={lines.refetch}
        />
      )}

      <Snackbar
        open={rebuildNotice !== null}
        autoHideDuration={rebuildNotice === 'error' ? null : 6000}
        onClose={(_event, reason) => {
          // A failure stays until dismissed — a click elsewhere must not hide it.
          if (reason === 'clickaway' && rebuildNotice === 'error') return;
          setRebuildNotice(null);
        }}
      >
        <Alert
          severity={shownNotice === 'error' ? 'error' : 'success'}
          onClose={() => setRebuildNotice(null)}
        >
          {shownNotice === 'error' ? (
            <>
              {t('reports.freshness.rebuildError', 'Could not start a rebuild.')}
              <ErrorDetails
                error={rebuildError}
                headline={t('reports.freshness.rebuildError', 'Could not start a rebuild.')}
              />
            </>
          ) : (
            t(
              'reports.freshness.rebuildQueued',
              'Rebuild queued. Numbers will refresh when the rollup finishes.',
            )
          )}
        </Alert>
      </Snackbar>
    </Box>
  );
}
